import { ApiError, getJson, postJsonWithStatus } from './client';
import { ApiEnvelope, isApiEnvelope } from './migrations';

// FD-286 — the FD-ADR-021 wave lifecycle. All three endpoints have existed
// since FD-135/FD-185/FD-226 and have been **Postman-only** until now.
//
// FD-304 (FD-ADR-032) adds the guided path. The console still cannot MINT — the
// mint needs an interactive sign-in as the destination tenant's BPRT minting
// service account, which is a human at a keyboard. What changed is where the
// resulting DRS access token lives: the Manager redeems the grant server-side,
// so **the token never enters the browser**. The console handles only a
// `deviceCode`, which is a redemption handle and worthless until that human
// finishes signing in.
//
// The paste path stays first-class and is the only path when a deployment sets
// `DRS_DEVICE_CODE_ENABLED=false` (device-code flow is blockable per tenant by
// Conditional Access). A pasted token is held in component state for as long as
// the dialog is open, sent once, and never stored, logged, or put in a URL.

/** `POST /v1/migrations/{id}/mint-session` — 200 data (FD-302). */
export interface MintSession {
  migrationId: string;
  /** The resolved DESTINATION tenant. Server-derived; never sent by the client. */
  tenantId: string;
  /**
   * Which account to sign in as. The direct prevention for `AADSTS240005` —
   * signing in as the wrong account is the most common way a mint fails.
   *
   * Since FD-439 (`FD-ADR-042` §5) this is the account RESOLVED for the migration's
   * own destination tenant — `Tenants.BprtServiceAccountUpn` → the deployment-wide
   * `BPRT_SERVICE_ACCOUNT_UPN` → empty. It previously returned the deployment-wide
   * setting regardless of which tenant had just been resolved, which named the wrong
   * account the moment a deployment had two destinations.
   *
   * Empty when neither source carries one; the server never fabricates one, so the
   * UI must render the empty case rather than a placeholder — and must never
   * reconstruct the value client-side, which would be a second resolver that can
   * disagree with the one that actually mints.
   */
  serviceAccountUpn: string;
  clientId: string;
  /** What the operator types at `verificationUrl`. */
  userCode: string;
  verificationUrl: string;
  /**
   * An OAuth grant HANDLE, not a credential: single-use, ~15 minutes, bound to
   * one client and resource, and inert until the paired `userCode` sign-in
   * completes. It is the direct analogue of an authorization code, which is
   * why it is allowed to transit the browser when the access token is not
   * (FD-ADR-032 §3, including its stated caveat).
   */
  deviceCode: string;
  expiresInSeconds: number;
  intervalSeconds: number;
  /**
   * ABSOLUTE server-computed expiry. The poll deadline anchors on this, not on
   * `expiresInSeconds`, because the client cannot know how much of the window
   * the round trip consumed.
   */
  expiresUtc: string;
}

export async function beginMintSession(migrationId: string): Promise<MintSession> {
  const { body } = await postJsonWithStatus<ApiEnvelope<MintSession>, Record<string, never>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/mint-session`,
    {},
  );
  return body.data;
}

// ─── FD-592 (FD-ADR-047 §3): the BROWSER sign-in ─────────────────────────────
//
// The same first-party client and the same DRS resource as the device-code flow,
// signed in through an authorization code + PKCE with a LOOPBACK redirect. No app
// Rubix or a customer owns can request the DRS scope (FD-ADR-047, the constraint
// stated first), so the redirect is Microsoft's: `http://localhost:<port>/`, which
// nothing serves. The operator either lets the sign-in helper extension carry the
// code back, or pastes the address the tab landed on.
//
// What the browser holds: the `state` (a CSRF value it compares), the redirect URI
// (non-secret), and the PKCE VERIFIER — in component memory only. What it never
// holds: the DRS access token. The Manager redeems the code with the verifier and
// mints inside one `activate-wave` call.

/** `POST /v1/migrations/{id}/mint-session` with `{mode:"redirect"}` — 200 data (FD-591). */
export interface BrowserMintSession {
  migrationId: string;
  /** The resolved DESTINATION tenant. Server-derived. */
  tenantId: string;
  /** Which account to sign in as — see `MintSession.serviceAccountUpn`. Empty when none resolves. */
  serviceAccountUpn: string;
  clientId: string;
  mode: 'redirect';
  /** Microsoft's v2 authorize URL. Complete when `codeChallengeIncluded`. */
  authorizeUrl: string;
  /**
   * Whether the server put OUR challenge on `authorizeUrl`. The console always sends
   * one; `false` would mean a Manager that ignored it, and the console then appends
   * it itself rather than open a sign-in whose code its verifier cannot redeem.
   */
  codeChallengeIncluded: boolean;
  /** 128 random bits, base64url. Compared by the console AND by the server on a paste. */
  state: string;
  /** `http://localhost:<random port>/` — sent back verbatim on the helper path. */
  redirectUri: string;
  scope: string;
  /** How long Entra's authorization code lives once issued (informational). */
  codeLifetimeSeconds: number;
}

export async function beginBrowserMintSession(
  migrationId: string,
  codeChallenge: string,
): Promise<BrowserMintSession> {
  const { body } = await postJsonWithStatus<
    ApiEnvelope<BrowserMintSession>,
    { mode: 'redirect'; codeChallenge: string }
  >(`/v1/migrations/${encodeURIComponent(migrationId)}/mint-session`, {
    mode: 'redirect',
    codeChallenge,
  });
  return body.data;
}

/**
 * FD-341a (FD-332) — which tenant issued the credential ATC will actually check.
 *
 * `mintIdentity` on `bprt.wave.activated` already records a `tid`, but it comes
 * from the **DRS access token** — the credential the operator presents *to* DRS.
 * ATC reads a different token: the `id_token` DRS mints *alongside* the BPRT.
 * Two tokens, two issuance events, very likely the same tenant — and "very
 * likely" is the inference this field exists to replace with a measurement.
 *
 * RECORDED, NEVER ASSERTED. A mismatch does not block activation, does not fail
 * the mint, and changes no status code. The console's job is to say so.
 */
export interface AssertionIdentity {
  /** The `id_token`'s `tid`, or null when the assertion was absent/undecodable. */
  tid: string | null;
  /** The `id_token`'s `iat`, in unix seconds. */
  iat: number | null;
  /**
   * Server-computed against the resolved destination tenant, deliberately: the
   * operator must never be asked to eyeball two GUIDs. Null when `tid` is null.
   */
  matchesDestination: boolean | null;
  /** A machine code when `tid` is null — e.g. `NO_ASSERTION`, `UNDECODABLE`. */
  reason: string | null;
}

/** `POST /v1/migrations/{id}/activate-wave` — 200 data. */
export interface ActivateWaveResult {
  migrationId: string;
  bprtStatus: string;
  expiresUtc: string;
  packageUserPresent: boolean;
  /**
   * OPTIONAL on purpose, and it must stay that way. FD-341a may not be deployed
   * against the console that renders this, and `useDeviceCodeActivation`'s
   * reconcile path builds a result from the migration row, where no assertion
   * was ever observed. Absent means **render nothing** — not an error, not an
   * empty row.
   */
  assertionIdentity?: AssertionIdentity | null;
}

interface ActivateWaveCommon {
  /** 1–90; the server defaults to 30. */
  expiryDays?: number;
}

/** Guided: the Manager redeems the code, so no token reaches the browser. */
export interface ActivateWaveBrokered extends ActivateWaveCommon {
  deviceCode: string;
  drsAccessToken?: never;
  returnUrl?: never;
  authorizationCode?: never;
}

/** Pasted: an operator-obtained DRS token. Retained indefinitely (FD-ADR-032 §6). */
export interface ActivateWavePasted extends ActivateWaveCommon {
  drsAccessToken: string;
  deviceCode?: never;
  returnUrl?: never;
  authorizationCode?: never;
}

/**
 * Browser sign-in, PASTED RETURN ADDRESS (FD-591). The Manager extracts the code and
 * compares the address's `state` with this one (`400 STATE_MISMATCH`).
 */
export interface ActivateWaveBrowserReturn extends ActivateWaveCommon {
  returnUrl: string;
  state: string;
  codeVerifier: string;
  redirectUri?: string;
  deviceCode?: never;
  drsAccessToken?: never;
  authorizationCode?: never;
}

/**
 * Browser sign-in, delivered by the HELPER (FD-591). No state is sent: the extension
 * and this console both matched it before the call (FD-ADR-047 §4), and the server
 * has no returned state to compare. `redirectUri` is mandatory — the redeem must
 * present exactly what the authorize request carried.
 */
export interface ActivateWaveBrowserCode extends ActivateWaveCommon {
  authorizationCode: string;
  codeVerifier: string;
  redirectUri: string;
  deviceCode?: never;
  drsAccessToken?: never;
  returnUrl?: never;
}

/**
 * A discriminated one-of, mirroring the server: supplying two credential sources is
 * `400 CREDENTIAL_SOURCE_AMBIGUOUS` and none is `400 CREDENTIAL_SOURCE_REQUIRED`.
 * The `?: never` members make that a compile error rather than a round trip.
 */
export type ActivateWaveRequest =
  | ActivateWaveBrokered
  | ActivateWavePasted
  | ActivateWaveBrowserReturn
  | ActivateWaveBrowserCode;

export async function activateWave(
  migrationId: string,
  req: ActivateWaveRequest,
): Promise<ActivateWaveResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<ActivateWaveResult>, ActivateWaveRequest>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/activate-wave`,
    req,
  );
  return body.data;
}

/** `POST /v1/migrations/{id}/complete-wave` — 200 data. */
export interface CompleteWaveResult {
  migrationId: string;
  bprtStatus: string;
}

export async function completeWave(migrationId: string): Promise<CompleteWaveResult> {
  const { body } = await postJsonWithStatus<ApiEnvelope<CompleteWaveResult>, unknown>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/complete-wave`,
    {},
  );
  return body.data;
}

/** `POST /v1/migrations/{id}/abort-wave` — 200 or **207** when some rows failed. */
export interface AbortWaveResult {
  status: number;
  migrationId: string;
  bprtStatus: string;
  /** Devices that demonstrably never started, now terminal-stamped `aborted`. */
  aborted: string[];
  /**
   * Devices left ALONE, and reported. Past the PONR the device owns its own
   * lifecycle and is not steerable (FA-ADR-021 / FD-ADR-025) — stamping these
   * would make the row assert a state the server cannot cause.
   */
  inProgress: string[];
  alreadyTerminal: string[];
  failed: string[];
  licenseSettle?: unknown;
}

export async function abortWave(
  migrationId: string,
  reason?: string,
): Promise<AbortWaveResult> {
  const { status, body } = await postJsonWithStatus<
    ApiEnvelope<Omit<AbortWaveResult, 'status'>>,
    { reason?: string }
  >(`/v1/migrations/${encodeURIComponent(migrationId)}/abort-wave`, reason ? { reason } : {});
  return { status, ...body.data };
}

/**
 * The machine codes these endpoints return. Each is a genuinely different
 * operator situation, which is why they are named rather than collapsed into
 * "something went wrong":
 *
 * - `AUTHORIZATION_PENDING` — **not an error.** The operator has not finished
 *   signing in yet. It drives the waiting state and carries `retryAfterSeconds`.
 *   It arrives as a 409 because the request genuinely could not be completed —
 *   which is why it must be recognised by CODE and never by status alone.
 * - `LICENSE_POOL_EXHAUSTED` — the concurrency pool is full. Always on, never
 *   behind a flag. Seeing this means capacity, not a licensing outage. On the
 *   brokered path it also means a completed sign-in was spent for nothing.
 *   Since FD-ADR-041 it fires on `poolAvailable < N` (one slot per assigned
 *   device) rather than `< 1`.
 * - `LICENSE_CREDITS_EXHAUSTED` — FD-430 / FD-ADR-041 decision 7. The 1:1
 *   CREDIT bucket is short, **and this deployment has chosen to enforce it**
 *   (`LICENSING_ENFORCE_CONSUMABLE=true`, which ships `false`). It must never
 *   share a branch with the pool code: the pool is a platform capacity limit
 *   every deployment has, this is a commercial setting most deployments do not
 *   turn on, and the remedies differ. Like the pool block it is returned BEFORE
 *   the mint, so nothing was minted — but on the guided path the sign-in was
 *   still spent.
 * - `REVOKE_RETRY` — the `package_<pid>` user is not yet directory-visible.
 *   The wave is **not** revoked and the call should be retried. FD-185/FD-187
 *   exist precisely so this can never be reported as success: a wave shown as
 *   revoked while a live BPRT survives is the kill-switch silently no-opping.
 * - `DEVICE_CODE_*` — the guided sign-in itself. `CLIENT_REJECTED` and `BLOCKED`
 *   are tenant configuration, not campaign faults, and both have a documented
 *   remedy plus the paste path as a fallback.
 * - `DRS_TOKEN_*` — claim assertion on the acquired token (FD-ADR-032 §5),
 *   applied to BOTH paths before any mint is attempted.
 * - `BPRT_MINT_ROLE_MISSING` — sign-in succeeded, DRS refused: the account is
 *   not role-privileged (`AADSTS240005`).
 * - `BPRT_STORE_FAILED` — the sharpest one. A `package_` user now exists that
 *   AirBridge cannot revoke, so this needs a human, not a retry button.
 * - `MAPPINGS_FROZEN` — expected AFTER activation (FD-ADR-025 D4), not a fault.
 * - The FD-591 browser sign-in codes. `AUTHORIZATION_CODE_INVALID` means "sign in
 *   again" (the code expired or was used); `AUTHORIZATION_CODE_REJECTED` means a
 *   NEW sign-in will be refused the same way — a policy, not a stale code — so the
 *   copy points at the policy and the other two paths instead of a retry loop.
 *   `STATE_MISMATCH` is an address from a different sign-in. None of them minted.
 */
const WAVE_ERROR_CODES = [
  'AUTHORIZATION_PENDING',
  'AUTHORIZATION_DECLINED',
  'DEVICE_CODE_DISABLED',
  'DEVICE_CODE_CLIENT_REJECTED',
  'DEVICE_CODE_BLOCKED',
  'DEVICE_CODE_BEGIN_FAILED',
  'DEVICE_CODE_REDEEM_FAILED',
  'DEVICE_CODE_EXPIRED',
  'DESTINATION_TENANT_UNRESOLVED',
  'CREDENTIAL_SOURCE_AMBIGUOUS',
  'CREDENTIAL_SOURCE_REQUIRED',
  'DRS_TOKEN_MALFORMED',
  'DRS_TOKEN_WRONG_AUDIENCE',
  'DRS_TOKEN_WRONG_TENANT',
  'DRS_TOKEN_NOT_USER_CONTEXT',
  'DRS_TOKEN_EXPIRED',
  'LICENSE_POOL_EXHAUSTED',
  'LICENSE_CREDITS_EXHAUSTED',
  'BPRT_MINT_ROLE_MISSING',
  'BPRT_MINT_FAILED',
  'BPRT_STORE_FAILED',
  'BPRT_STATE_STAMP_FAILED',
  'REVOKE_RETRY',
  'MAPPINGS_FROZEN',
  // FD-592 — the browser sign-in (FD-591).
  'INVALID_MODE',
  'CODE_CHALLENGE_INVALID',
  'CODE_VERIFIER_INVALID',
  'RETURN_URL_INVALID',
  'STATE_REQUIRED',
  'STATE_MISMATCH',
  'REDIRECT_URI_MISMATCH',
  'REDIRECT_URI_INVALID',
  'AUTHORIZATION_REFUSED',
  'AUTHORIZATION_CODE_INVALID',
  'AUTHORIZATION_CODE_REJECTED',
  'AUTHORIZATION_CODE_REDEEM_FAILED',
] as const;

export type WaveErrorCode = (typeof WAVE_ERROR_CODES)[number] | 'UNKNOWN';

const KNOWN_WAVE_ERROR_CODES: ReadonlySet<string> = new Set(WAVE_ERROR_CODES);

export function waveErrorCode(err: unknown): WaveErrorCode {
  if (!(err instanceof ApiError)) return 'UNKNOWN';
  const code = isApiEnvelope(err.body) ? err.body.error?.code : undefined;
  return typeof code === 'string' && KNOWN_WAVE_ERROR_CODES.has(code)
    ? (code as WaveErrorCode)
    : 'UNKNOWN';
}

/**
 * The server's suggested delay before the next poll, in seconds, off the 409
 * envelope's `data`. `undefined` when absent — the caller keeps its own
 * interval rather than inventing one.
 */
export function waveRetryAfterSeconds(err: unknown): number | undefined {
  if (!(err instanceof ApiError) || !isApiEnvelope<{ retryAfterSeconds?: unknown }>(err.body)) {
    return undefined;
  }
  const value = err.body.data?.retryAfterSeconds;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;
}

/**
 * `true` when the failure is worth retrying as-is rather than re-diagnosing —
 * the keep-polling predicate. Both members are "the server has not finished,
 * ask again": one waits on a human, the other on Entra replication.
 */
export function isRetryableWaveError(err: unknown): boolean {
  const code = waveErrorCode(err);
  return code === 'AUTHORIZATION_PENDING' || code === 'REVOKE_RETRY';
}

// ─── FD-451: pre-activation minting-account readiness ───────────────────────
// `GET /v1/migrations/{id}/mint-readiness`.
//
// The FD-ADR-021 minting account's prerequisites, read BEFORE the operator
// starts an interactive sign-in rather than after DRS refuses one with an
// opaque `AADSTS240005`.
//
// It is a SIBLING of `mint-session`, not a field on it, because `mint-session`
// begins a device-authorization grant and opening a dialog must not mint a
// 900-second `device_code` the operator may never use.
//
// It NEVER gates activation. FD-ADR-023 §4 and FD-ADR-029 §3 both settled that
// tenant state is an operator action in someone else's tenant; and the probe is
// optimistic by design (see `caveats`), so blocking on it would trade a named
// error for a false block.

/** Where the resolved minting account came from — see `serviceAccountSource`. */
export type MintAccountSource = 'tenant' | 'deployment-default' | 'none';

/**
 * FD-591 (FD-ADR-047 §2) — one Conditional Access policy that blocks, or may block,
 * the DEVICE CODE flow for the minting account. Named so the Activate page can say
 * WHICH policy, rather than a bare `AADSTS530036` after the operator has typed a code.
 */
export interface DeviceCodeBlockingPolicy {
  id: string;
  displayName: string;
  /** `enabledForReportingButNotEnforced` is report-only: it logs, it does not block. */
  state: 'enabled' | 'enabledForReportingButNotEnforced' | string;
  /** `covers` — the account is in scope; `may-cover` — scope could not be decided. */
  certainty: 'covers' | 'may-cover' | string;
}

export interface MintReadinessAccount {
  present: boolean;
  /** e.g. `Password Administrator` — the least-privileged role DRS accepts. */
  role: string | null;
  /**
   * THREE meanings plus a fourth (FD-609), mirroring `BprtServiceAccountView`:
   *   `true`      — no blocking Conditional Access policy seen;
   *   `false`     — a policy may block the mint;
   *   `null`      — the probe could not verify the exclusion;
   *   `undefined` — a Manager predating FD-609 omitted it (treated as `null`;
   *                 such a Manager actually sent `false` for "could not verify").
   */
  caExclusionHealthy?: boolean | null;
  /**
   * THREE meanings plus a fourth, and they must stay distinct:
   *   `[]`        — checked, no device-code-blocking policy found;
   *   `[…]`       — these policies block (or may block) device code;
   *   `null`      — the probe could not determine it;
   *   `undefined` — the Manager predates FD-591 and never asked.
   */
  deviceCodeBlockedBy?: DeviceCodeBlockingPolicy[] | null;
}

export interface MintReadiness {
  migrationId: string;
  /** The resolved DESTINATION tenant. Server-derived, FD-ADR-029 aware. */
  tenantId: string;
  tenantDomain: string | null;
  serviceAccountUpn: string | null;
  /**
   * FD-450 is why this exists. On screen a deployment-wide default and a
   * per-tenant value were indistinguishable while meaning entirely different
   * things — and the default embeds ONE tenant's verified domain, so for any
   * other destination it named an account that cannot exist there.
   */
  serviceAccountSource: MintAccountSource;
  /** Whether the live probe ran at all. `false` is not a failure — see `checkReason`. */
  checked: boolean;
  checkReason: string | null;
  account: MintReadinessAccount | null;
  /**
   * The FD-442 blind spots, shipped ON THE WIRE beside the verdict rather than
   * left in a runbook. A panel that renders as a green light it cannot justify
   * is worse than no panel.
   */
  caveats: string[];
}

export async function getMintReadiness(migrationId: string): Promise<MintReadiness> {
  const envelope = await getJson<ApiEnvelope<MintReadiness>>(
    `/v1/migrations/${encodeURIComponent(migrationId)}/mint-readiness`,
  );
  if (!isApiEnvelope(envelope) || envelope.status !== 'success') {
    throw new ApiError(0, 'Could not read minting-account readiness', envelope);
  }
  return envelope.data;
}
